import { bookActions, loadingActions } from '@/actions';
import { bookConstants } from '@/constants';
import { ROUTE_BUILDER, toSlugId } from '@/constants/pages';
import { startOfDay } from '@/helpers/date';
import { isSistaminuten as isSistaminutenInstance } from '@/helpers/general';
import { getPlaceTimezone, hasDynamicPricing } from '@/helpers/placeHelper';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { hasDealCampagin } from '@/helpers/placeHelperTS';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import useWalletPayment from '@/hooks/useWalletPayment';
import { getLang } from '@/locale';
import { bookServices as bookServicesOld } from '@/services';
import { AppDispatch } from '@/store';
import { BookState } from '@/types/state/book';
import { PlaceService } from '@/types/state/shared';
import moment from 'moment';
import 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Availability, ErpTimeSlot, FromErpOverview } from './availability.types';
import {
  allowsGroupBookings,
  canGoNext as canGoNextWeek,
  canGoPrev as canGoPrevWeek,
  formatWeekRange,
  getOpenWeekdays,
  getWeekStart,
  getWeekStartOfDate,
  getWeeksCount,
  isNoTimesAtAll as resolveNoTimesAtAll,
  mergeOverviews,
  prepDate,
  toWallClockAsUtc,
  WEEK_MS,
} from './PickTime.helpers';
import { buildCalendar } from './PickTimeCalendar.helpers';
import { useMinimumDisplayTime } from './PickTimeLoading.hooks';
import { PickTimeProps, UsePickTimeArgs } from './PickTime.types';
import { buildWeek } from './PickTimeWeekView.helpers';

type GetAvailabilityArgs = {
  services: PlaceService[];
  date: number;
  employee: number;
  source: string;
  changeTime: boolean;
  bookingId?: number;
  dealId?: number;
  isSistaminuten?: boolean;
  shouldCommit?: () => boolean;
};

/**
 * A dispatchable thunk, tagged with what its dispatch resolves to. Nothing reads the
 * tag — it carries the resolution across the untyped boundary below, so the failure
 * guard is a real check rather than a comparison against `unknown`.
 */
type Thunk<TResolved> = { readonly __resolvesWith: TResolved };

/**
 * `bookActions` is untyped JS on two counts this container has to work around:
 * `getAvailability`'s inferred signature demands arguments this container leaves
 * unset (`bookingId` outside change-time), and the store's dispatch type is not
 * thunk-aware, so the thunk's promise (PR 1 made it return its chain) cannot be
 * awaited through it.
 */
const getAvailability = bookActions.getAvailability as unknown as (args: GetAvailabilityArgs) => Thunk<boolean>;
const dispatchThunk = <TResolved,>(dispatch: AppDispatch, thunk: Thunk<TResolved>): Promise<TResolved> =>
  (dispatch as unknown as (action: unknown) => Promise<TResolved>)(thunk);

/** The same untyped-defaults workaround as PickTime.helpers.ts' isoMondayOf. */
const startOfDayMs = startOfDay as (day?: number | false) => number;

/**
 * The pick-time container's data layer (MPA-2665).
 *
 * Owns availability fetching, the visible week anchor, the week/month view
 * switch and the slot → checkout handoff, and returns exactly the props
 * `PickTime` renders. The redux selection is read, never re-derived: legacy
 * entries into the calendar (search results, place page, deep links) carry none
 * of the `?ss/sr/rs/et` params, so running the URL-driven selection hook here
 * would pop every picked service.
 */
export function usePickTime({
  place,
  services,
  employee,
  time,
  campaignStart,
  campaignEnd,
  lastWorkingDays,
  isSistaminuten,
  onChooseAnotherService,
  onChangeEmployee,
  onOpenWaitlist,
  onSelectChangeTimeSlot,
  refetchKey,
}: UsePickTimeArgs): PickTimeProps {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<{ cancelBooking?: unknown } | undefined>();
  const { isMobileView } = useMobileView();
  const { hasActiveApplePayPaymentMethod, hasActiveGooglePayPaymentMethod } = useWalletPayment();
  const user = useAppSelector((state) => state.users.user);
  const availability = useAppSelector((state) => (state.book as BookState).availability) as Availability | undefined;
  const appliedBundle = useAppSelector((state) => (state.book as BookState).appliedBundle);
  const changeTimeBookingId = useAppSelector((state) => (state.book as BookState).changeTimeBookingId);

  const placeTimezone = getPlaceTimezone(place);
  // The anchor helpers (firstWeekDay/startOfDay) and both builders derive day-keys
  // from moment's ambient default, so it has to be the place's tz before the first
  // render's derivations run — otherwise an out-of-tz visitor anchors weeks against
  // their own midnight while the builders classify days against the place's.
  useMemo(() => moment.tz.setDefault(placeTimezone), [placeTimezone]);

  const [weekStart, setWeekStart] = useState(() =>
    getWeekStart(time?.timestamp && time.timestamp > startOfDay() ? time.timestamp : startOfDay()),
  );
  const [isMonthView, setIsMonthView] = useState(false);
  const [isPending, setIsPending] = useState(true);
  // The scope whose fetch has settled. `hasSettled` is derived from it below, so a
  // change of place / services / employee / deal reads as unsettled in the same
  // render — the previous query's terminal states must not outlive it — while week
  // navigation keeps the scope and so stays settled.
  const [settledScopeKey, setSettledScopeKey] = useState<string | undefined>(undefined);
  // The scope whose fetch has finished, whatever it resolved to. Finishing only means
  // nothing is in flight; settling means the store answered for this scope. The loading
  // view reads the first, every terminal state the second.
  const [finishedScopeKey, setFinishedScopeKey] = useState<string | undefined>(undefined);
  // The scope whose landed fetch left the auto-jump to decide, and with it the finish.
  const [autoJumpDecisionScopeKey, setAutoJumpDecisionScopeKey] = useState<string | undefined>(undefined);
  // `changeTime: true` resets the picked hour and rewrites the stored bookDate, so
  // only the initial load may carry it — a week change must never reset the hour.
  const isFirstFetchRef = useRef(true);
  const requestIdRef = useRef(0);
  // The scope whose settled fetch has not been folded in yet; `availability`
  // carries no marker of the query it was fetched for.
  const settledScopeRef = useRef<string | undefined>(undefined);
  // The scope whose first fetch has finished, and with it the one chance to auto-jump.
  const autoJumpScopeRef = useRef<string | undefined>(undefined);
  // The auto-jump the week on screen came from, until the customer navigates.
  const autoJumpedRef = useRef<{ scopeKey: string; from: number; to: number } | undefined>(undefined);

  const dealId = hasDealCampagin(place?.campaigns ?? [])?.id;
  // The legacy deal calendar's auto-jump: deal availability is often sparse or starts
  // weeks out, so a deal booking opens on its first available week rather than an
  // empty one. The legacy last-minute picker never jumped.
  const autoJumpsToFirstAvailable = Boolean(dealId) && !isSistaminuten;
  const serviceIdsKey = (services ?? []).map((service) => service.id).join('-');
  // The query an overview belongs to. A response fetched for another place,
  // service set, employee or deal describes a different set of open days, so
  // nothing pinned may cross one.
  const scopeKey = `${place?.id ?? ''}|${serviceIdsKey}|${employee || 0}|${dealId ?? ''}`;
  // The escape hatch out of the last-minute flow, offered only on sistaminuten.se:
  // for a lastMinuteDiscount place on bokadirekt.se it would point at the site the
  // visitor is already on. There is no config key for the origin. Withheld in change-time: a
  // regular time picked there would be a second booking, not a move of this one.
  const regularTimesUrl =
    isSistaminutenInstance() && !changeTimeBookingId
      ? `https://www.bokadirekt.se${ROUTE_BUILDER.placePage(toSlugId(place?.about?.slug, place.id))}`
      : undefined;
  const hasSettled = settledScopeKey === scopeKey;
  const hasFinished = finishedScopeKey === scopeKey;

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    // A week the auto-jump chose for another scope says nothing about this one, and
    // the ERP only looks forward from the fetched week, so a fetch anchored there
    // would hide this scope's earlier availability. It restarts from the week the
    // jump left instead, and decides its own jump from there.
    const autoJumped = autoJumpedRef.current;
    if (autoJumped && autoJumped.scopeKey !== scopeKey && autoJumped.to === weekStart) {
      goToWeek(autoJumped.from);
      return;
    }
    const date = prepDate(weekStart);
    // Only a fetch anchored on today may carry the reset: `changeTime` writes
    // `{ timestamp: date, selected: '' }` over `book.time` wholesale, so a later
    // week's fetch would hand the flow that week's Monday as the booking date,
    // which campaign windows and the add-service fetch then read.
    const changeTime = isFirstFetchRef.current && date === startOfDayMs();
    setIsPending(true);
    // The add/remove-service and change-employee controls stay live while a fetch is
    // in flight, so two requests for different scopes can overlap and resolve in
    // either order. The reducer writes a response's services and slots wholesale,
    // so a superseded one landing last would revert the selection (or leave the
    // previous employee's slots under the new employee); the thunk drops it instead.
    const isCurrentRequest = () => requestIdRef.current === requestId;

    const fetchAvailability = async () => {
      // The dispatch rejects on paths that have nothing to do with the request:
      // `success` writes sessionStorage synchronously in the thunk's resolution
      // handler, so a storage throw takes the promise down with it. Left unhandled
      // that would keep the loading view past the fetch, with nothing to end it.
      const didSucceed = await dispatchThunk(
        dispatch,
        getAvailability({
          services,
          date,
          employee: employee || 0,
          source: 'loadHours',
          changeTime,
          // The booking being moved keeps its seat, so the ERP offers only the slots it fits.
          bookingId: changeTimeBookingId,
          dealId,
          isSistaminuten,
          shouldCommit: isCurrentRequest,
        }),
      ).catch(() => false);
      // A superseded request must not clear the pending flag or mark the scope
      // settled while the newer fetch is still in flight.
      if (!isCurrentRequest()) return;
      // Only the scope's first finished fetch may jump: after a failure the customer's
      // own navigation is the retry, and a jump landing then would override it. A fetch
      // that decides the jump leaves the scope unfinished, so the loading view spans
      // the jumped week's fetch instead of handing back the empty entry week between.
      const decidesAutoJump = didSucceed && autoJumpsToFirstAvailable && autoJumpScopeRef.current !== scopeKey;
      autoJumpScopeRef.current = scopeKey;
      if (decidesAutoJump) setAutoJumpDecisionScopeKey(scopeKey);
      else setFinishedScopeKey(scopeKey);
      // A failure finishes the scope without settling it: the store still holds the
      // previous query's response, or nothing at all on first load, and settling on
      // that reads as "this scope is empty" — the terminal no-times screen and its
      // waitlist entry, off a dropped request. Finishing hands back the week grid,
      // whose header and arrows stay live, so navigating is the retry; `isPending`
      // stays set so the grid holds its own loading state rather than classifying
      // days off a response that never arrived.
      if (!didSucceed) return;
      // Spent by the fetch that lands, not the one that is issued: a failed or refused
      // fetch reset no hour and rewrote no bookDate, so the reset is still owed to
      // whichever fetch anchored on today answers first.
      if (changeTime) isFirstFetchRef.current = false;
      settledScopeRef.current = scopeKey;
      setIsPending(false);
      setSettledScopeKey(scopeKey);
    };

    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, employee, serviceIdsKey, dealId, place?.id, refetchKey]);

  // The ERP recomputes the overview from each fetch's anchor week, and
  // SERVICE_PROGRAM replaces it wholesale, so a later week's response can arrive
  // naming only that window's open days, or none at all. Folding each response
  // into the pinned one keeps the horizon legacy holds across such a response,
  // instead of letting it fall back to the wider bookAhead bound and re-enable
  // the next arrow past it.
  //
  // Only a response fetched for the current scope may be folded in: redux still
  // holds the previous query's response until the refetch lands.
  const liveOverview = availability?.fromErpOverview;
  const pinnedOverviewRef = useRef<{ scopeKey: string; overview?: FromErpOverview }>({ scopeKey });
  // The reducer hands out a new overview per response, so its identity is what
  // separates a fresh one from the re-renders that follow it. Folding the same
  // response twice would match its own window and replace the union it just
  // contributed to.
  const foldedOverviewRef = useRef<FromErpOverview | undefined>(undefined);
  if (pinnedOverviewRef.current.scopeKey !== scopeKey) {
    pinnedOverviewRef.current = { scopeKey };
    // Redux still holds the response fetched for the scope being left, and a refetch
    // that fails leaves it there for good, so it counts as already folded: only an
    // object this scope's own response put there may reach the pin.
    foldedOverviewRef.current = liveOverview;
    settledScopeRef.current = undefined;
  }
  if (settledScopeRef.current === scopeKey && liveOverview !== foldedOverviewRef.current) {
    foldedOverviewRef.current = liveOverview;
    // One fold per settled fetch: only the response that settle announced may
    // reach the pin.
    settledScopeRef.current = undefined;
    pinnedOverviewRef.current.overview = mergeOverviews(pinnedOverviewRef.current.overview, liveOverview);
  }
  const horizonOverview = pinnedOverviewRef.current.overview;

  const weeksCount = getWeeksCount({ place, lastWorkingDays, overview: horizonOverview, isSistaminuten });
  const canGoPrev = canGoPrevWeek(weekStart);
  const canGoNext = canGoNextWeek(weekStart, weeksCount);
  const nextAvailableDate = availability?.fromErpOverview?.firstAvailableDate;
  const nextAvailableWeek = nextAvailableDate ? getWeekStartOfDate(nextAvailableDate) : undefined;
  // A jump to the week on screen changes no fetch dep, so it would leave the view loading for good.
  // A date the moved booking may not fit could land on another empty week, so the customer pages
  // forward with the arrows instead. Change-time trusts only a date the backend vouched for: a
  // response without the flag counts as one that may not fit.
  const nextAvailableDateFits =
    !changeTimeBookingId || availability?.fromErpOverview?.firstAvailableDateMayNotFit === false;
  const offeredNextAvailableWeek =
    nextAvailableWeek !== undefined && nextAvailableWeek > weekStart && nextAvailableDateFits
      ? nextAvailableWeek
      : undefined;

  // Navigation only moves the anchor; the effect above owns the refetch. Every
  // anchor move goes through here so the pending flag is set in the same batch,
  // leaving the weekModel guard below already pending on the new anchor's first
  // render rather than one render later, once the effect gets to run.
  const goToWeek = useCallback((next: number | ((current: number) => number)) => {
    autoJumpedRef.current = undefined;
    setIsPending(true);
    setWeekStart(next);
  }, []);

  const onPrev = useCallback(() => {
    if (canGoPrev) goToWeek((current) => current - WEEK_MS);
  }, [canGoPrev, goToWeek]);

  const onNext = useCallback(() => {
    if (canGoNext) goToWeek((current) => current + WEEK_MS);
  }, [canGoNext, goToWeek]);

  const onJumpToNextAvailable = useCallback(() => {
    if (offeredNextAvailableWeek !== undefined) goToWeek(offeredNextAvailableWeek);
  }, [offeredNextAvailableWeek, goToWeek]);

  // Set in the same batch as the settle, so the render that reads it is the first to
  // hold the deciding fetch's response.
  useEffect(() => {
    if (autoJumpDecisionScopeKey === undefined) return;
    setAutoJumpDecisionScopeKey(undefined);
    // A decision owed to a scope the customer has since left is dropped: that scope
    // decides again on its next landed fetch.
    if (autoJumpDecisionScopeKey !== scopeKey) return;
    // Only to the week the pointer would offer, so forward only: an anchor on a picked time
    // keeps its week while it has availability. A week past the horizon is one the arrows
    // could never reach.
    const isReachable =
      offeredNextAvailableWeek !== undefined && canGoNextWeek(offeredNextAvailableWeek - WEEK_MS, weeksCount);
    if (isReachable) {
      goToWeek(offeredNextAvailableWeek);
      autoJumpedRef.current = { scopeKey, from: weekStart, to: offeredNextAvailableWeek };
    } else {
      setFinishedScopeKey(scopeKey);
    }
  }, [autoJumpDecisionScopeKey, scopeKey, offeredNextAvailableWeek, weeksCount, weekStart, goToWeek]);

  const onToggleMonthView = useCallback((on: boolean) => setIsMonthView(on), []);

  // Legacy day → week redirect: the month view has no per-day slots, so picking a
  // day returns to the week grid anchored on that day's week.
  const onSelectDay = useCallback(
    (date: string) => {
      const target = getWeekStartOfDate(date);
      // The store already holds the anchored week's slots, and a same-week move
      // changes no fetch dep, so the pending flag goToWeek sets would never clear.
      if (target !== weekStart) goToWeek(target);
      setIsMonthView(false);
    },
    [goToWeek, weekStart],
  );

  const onSelectSlot = useCallback(
    async (slot: ErpTimeSlot) => {
      // Keyed on the store, not on the callback: a pick in change-time must never reach the
      // checkout, which would book the slot a second time instead of moving the booking.
      if (changeTimeBookingId) {
        onSelectChangeTimeSlot?.(slot);
        return;
      }
      const timeSlotEmployee = (place?.employees ?? []).filter((e) => e.id === slot.employeeId)[0];
      const timeSlotMoment = moment(slot.start);
      const timeSlotUnix = toWallClockAsUtc(slot.start).unix();
      const startOfDayUnix = moment.utc(timeSlotMoment).startOf('day').unix();
      const startOfDayUnixOld = startOfDayMs(startOfDayUnix * 1000);
      const hour = timeSlotUnix - startOfDayUnix;

      let dynamicPriceListIdKey = bookConstants.DYNAMIC_PRICE_KEY_DEFAULT;
      if ((services ?? []).some((service) => hasDynamicPricing(service))) {
        dispatch(loadingActions.show('loadHours'));
        try {
          const hasDynamicPrice = await bookServicesOld.hasDynamicPrice(place.id, slot.start);
          if (hasDynamicPrice?.dynamicPriceListIdKey) dynamicPriceListIdKey = hasDynamicPrice.dynamicPriceListIdKey;
        } catch (e) {
          // A failed lookup keeps the default key rather than blocking the booking.
        }
        dispatch(loadingActions.hide());
      }

      dispatch(
        bookActions.pickHour(startOfDayUnixOld, hour, [timeSlotEmployee], dynamicPriceListIdKey, [], slot.capacity),
      );
      dispatch(bookActions.setCampaigns(services, place?.campaigns, startOfDayUnixOld));

      const showSelectedEmployee = Boolean(place?.about?.book?.showSelectedEmployee);
      const useRandomEmployee = Boolean(!employee && !showSelectedEmployee);
      bookingFlowTrackingSession.setProperties({
        employee_selection: useRandomEmployee ? 'any' : 'specific',
        // Mirrors checkoutFlowTrackingSession's own precedence (place.lastMinuteDiscount
        // ahead of a deal), so this step cannot disagree with the property the session's
        // `initiated` step already set.
        booking_type: place?.lastMinuteDiscount ? 'sistaminuten' : dealId ? 'deal' : 'marketplace',
      });
      bookingFlowTrackingSession.pickTime();

      history.push({
        pathname: '/booking/checkout',
        state: {
          employee: timeSlotEmployee,
          fromUrl: window.location.href,
          user,
          hasActiveGooglePayPaymentMethod,
          hasActiveApplePayPaymentMethod,
          cancelBooking: location.state?.cancelBooking,
        },
      });
    },
    [
      dispatch,
      history,
      location.state,
      place,
      services,
      employee,
      dealId,
      user,
      hasActiveApplePayPaymentMethod,
      hasActiveGooglePayPaymentMethod,
      changeTimeBookingId,
      onSelectChangeTimeSlot,
    ],
  );

  // Week navigation must not flash the loading view, so it is gated on the scope's
  // fetch finishing: a later week's fetch keeps the previous week on screen while it
  // is in flight, where a scope change loads until its own fetch is over.
  const isLoading = useMinimumDisplayTime(!hasFinished);

  // Change-time highlights no campaign days, as the legacy picker did. Its slot prices stay: the
  // move re-applies whichever campaign covers the new time.
  const styledCampaignStart = changeTimeBookingId ? undefined : campaignStart;
  const styledCampaignEnd = changeTimeBookingId ? undefined : campaignEnd;
  const openWeekdays = getOpenWeekdays(place?.program);

  const rawWeekModel = buildWeek(availability, {
    weekStart,
    referenceTime: Date.now(),
    placeTimezone,
    campaignStart: styledCampaignStart,
    campaignEnd: styledCampaignEnd,
    // Changing an existing booking's time keeps that booking's seat, so its slots
    // are filtered to ones that fit it and their capacity is not on offer.
    showSpotsLeft: !changeTimeBookingId && allowsGroupBookings(services, appliedBundle),
    hidePrices: Boolean(isSistaminuten),
    openWeekdays,
  });
  // The pointer and its jump button show only for a date the jump would actually move to.
  const scopedWeekModel =
    offeredNextAvailableWeek === undefined ? { ...rawWeekModel, nextAvailableDate: undefined } : rawWeekModel;
  // weekStart moves a render before the new week's slots arrive, so an unfetched
  // week reads as genuinely empty and each of its days as fully booked — mark the
  // model loading rather than render availability that has not been fetched.
  const weekModel = isPending ? { ...scopedWeekModel, state: 'loading' as const } : scopedWeekModel;
  // No range override: the month grid spans the checked horizon, which need not
  // agree with the `weeksCount` horizon the week arrows use — the calendar has no
  // navigation of its own.
  const calendarData = buildCalendar(availability, {
    referenceTime: Date.now(),
    placeTimezone,
    campaignStart: styledCampaignStart,
    campaignEnd: styledCampaignEnd,
    overview: horizonOverview,
    slotWeekStart: weekStart,
    openWeekdays,
  });

  // Preserves the `timeslots_available` property that `choose_time` carries.
  // `weekModel` is rebuilt every render, so the settled availability is the dep —
  // appendTimeslotsViewed de-dupes internally.
  useEffect(() => {
    if (!hasSettled) return;
    bookingFlowTrackingSession.appendTimeslotsViewed(
      weekModel.days.flatMap((day) => day.slots.map((slot) => slot.raw.start)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSettled, availability]);

  return {
    isLoading,
    // Settling is per scope, not per week: while this anchor's fetch is in flight, or
    // after it failed, the store holds another week's response, which says nothing
    // about whether this one is empty.
    isNoTimesAtAll: resolveNoTimesAtAll({ availability, hasSettled: hasSettled && !isPending, weekStart }),
    isMonthView,
    isNarrow: isMobileView,
    variant: isSistaminuten ? 'smt' : 'default',
    // The calendar has no loading state of its own: before this scope's store answers
    // it would paint every day unavailable, or the previous scope's open days as this one's.
    canToggleMonthView: !isSistaminuten && hasSettled,
    regularTimesUrl,
    weekRangeTitle: formatWeekRange(weekStart, getLang()),
    weekModel,
    calendarData,
    canGoPrev,
    canGoNext,
    onPrev,
    onNext,
    onJumpToNextAvailable,
    onToggleMonthView,
    onSelectDay,
    onSelectSlot,
    onChooseAnotherService,
    onChangeEmployee,
    onOpenWaitlist,
  };
}
